import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getTodayBookings, getBookings, updateFullBooking, deleteBooking } from "../../../../api/bookingApi";
import { getTables } from "../../../../api/tableAPI";
import { getCustomerReport } from "../../../../api/reportApi";
import BookingRow from "../Booking/BookingRow";
import BookingModal from "../Booking/BookingModal";
import CustomerModal from "../Customer/CustomerModal";
import Pagination from "../Pagination/Pagination";
import { MODAL_MODES } from "../../../../constants/enums";
import { translateMembershipLevel } from "../../../../utils/stranslateUltils";
import '../css/Dashboard.css'
import { FaExclamationTriangle } from "react-icons/fa";

const Dashboard = ({ onNavigateToBookings, onNavigateToTables, onNavigateToCustomer }) => {
  const { t } = useTranslation();

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    pendingBookings: 0,
    todayBookings: [],
    availableTables: 0,
    newCustomersThisMonth: 0,
    topCustomers: []
  });
  
  // Pagination state for today's bookings
  const [todayBookingsPage, setTodayBookingsPage] = useState(1);
  const [todayBookingsPageSize, setTodayBookingsPageSize] = useState(5);
  const [todayBookingsTotalPages, setTodayBookingsTotalPages] = useState(0);
  const [todayBookingsTotalItems, setTodayBookingsTotalItems] = useState(0);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state for booking details
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingModalMode, setBookingModalMode] = useState("");
  
  // Modal state for customer details
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerModalMode, setCustomerModalMode] = useState("");
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  
  const isBookingModalOpen = !!selectedBooking;
  const isCustomerModalOpen = !!selectedCustomer;
  
  const closeBookingModal = () => {
    setSelectedBooking(null);
    setBookingModalMode("");
  };
  
  const openCustomerModal = (customer, mode) => {
    setSelectedCustomer(customer);
    setCustomerModalMode(mode);
  };
  
  const closeCustomerModal = () => {
    setSelectedCustomer(null);
    setCustomerModalMode("");
  };
    
  // Enhanced handlers for BookingRow actions (similar to RecentBookingsTable)
  const handleShowDetails = (booking) => {
    setSelectedBooking(booking);
    setBookingModalMode(MODAL_MODES.VIEW);
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setBookingModalMode(MODAL_MODES.EDIT);
  };

  const handleDelete = (booking) => {
    setBookingToDelete(booking);
    setShowDeleteConfirm(true);
  };

  const handleSaveBooking = async (updatedBooking) => {
    try {
      const response = await updateFullBooking(updatedBooking.bookingId, updatedBooking);
      
      // Update the booking in the today's bookings list
      setDashboardData(prevData => ({
        ...prevData,
        todayBookings: prevData.todayBookings.map(booking =>
          booking.bookingId === response.bookingId ? response : booking
        )
      }));
      
      closeBookingModal();
      return response;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  };

  const handleDeleteFromModal = async (booking) => {
    try {
      await deleteBooking(booking.bookingId);
      
      // Remove booking from the today's bookings list
      setDashboardData(prevData => ({
        ...prevData,
        todayBookings: prevData.todayBookings.filter(b => b.bookingId !== booking.bookingId)
      }));
      
      // Update total items count
      setTodayBookingsTotalItems(prev => Math.max(prev - 1, 0));
      
      closeBookingModal();
      
      // Reload today's bookings to maintain pagination consistency
      await loadTodayBookings(todayBookingsPage, todayBookingsPageSize);
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (bookingToDelete) {
      try {
        await deleteBooking(bookingToDelete.bookingId);
        
        // Remove booking from the today's bookings list
        setDashboardData(prevData => ({
          ...prevData,
          todayBookings: prevData.todayBookings.filter(booking => booking.bookingId !== bookingToDelete.bookingId)
        }));
        
        // Update total items count
        setTodayBookingsTotalItems(prev => Math.max(prev - 1, 0));
        
        // Reload today's bookings to maintain pagination consistency
        await loadTodayBookings(todayBookingsPage, todayBookingsPageSize);
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
      
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  // Load today's bookings with pagination
  const loadTodayBookings = async (page = 1, size = 5) => {
    try {
      const response = await getTodayBookings({
        page: page - 1, // Backend expects 0-based page index
        size: size
      });
      
      if (response && Array.isArray(response.content)) {
        setDashboardData(prev => ({
          ...prev,
          todayBookings: response.content
        }));
        setTodayBookingsTotalPages(response.totalPages);
        setTodayBookingsTotalItems(response.totalElements);
        setTodayBookingsPage(page);
      } else {
        // Handle case where response structure is different
        setDashboardData(prev => ({
          ...prev,
          todayBookings: []
        }));
        setTodayBookingsTotalPages(0);
        setTodayBookingsTotalItems(0);
      }
    } catch (err) {
      console.error("Error loading today's bookings:", err);
      setDashboardData(prev => ({
        ...prev,
        todayBookings: []
      }));
      setTodayBookingsTotalPages(0);
      setTodayBookingsTotalItems(0);
    }
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      const [pendingBookingsRes, tablesRes, customerReportRes] = await Promise.all([
        getBookings({ status: "PENDING", page: 0, size: 5 }),
        getTables({ status: "AVAILABLE" }),
        getCustomerReport({ year: currentYear, month: currentMonth })
      ]);

      const pendingCount = pendingBookingsRes.totalElements;
      const availableTablesCount = Array.isArray(tablesRes) ? tablesRes.length : 0;
      const newCustomersCount = customerReportRes.totalNewCustomers;
      const topCustomers = Array.isArray(customerReportRes?.topCustomers)
        ? customerReportRes.topCustomers
        : [];

      setDashboardData(prev => ({
        ...prev,
        pendingBookings: pendingCount,
        availableTables: availableTablesCount,
        newCustomersThisMonth: newCustomersCount,
        topCustomers: topCustomers
      }));

      await loadTodayBookings(1, todayBookingsPageSize);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setError(t("admin.dashboard.error_desc"));
    } finally {
      setLoading(false);
    }
  }, [todayBookingsPageSize,t]);
  
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handlePendingBookingsClick = () => {
    // Navigate to bookings page with today's filter
    onNavigateToBookings("today");
  };
  
  const handleAvailableTablesClick = () => {
    // Navigate to tables page
    onNavigateToTables();
  };

  const handleCustomersClick = () => {
    // Navigate to tables page
    onNavigateToCustomer();
  };

  const handleViewCustomerDetails = (customer) => {
    // Open customer modal in view mode
    openCustomerModal(customer, MODAL_MODES.VIEW);
  };

  // Pagination handlers for today's bookings
  const handleTodayBookingsPageChange = (page) => {
    loadTodayBookings(page, todayBookingsPageSize);
  };

  const handleTodayBookingsPageSizeChange = (newSize) => {
    setTodayBookingsPageSize(newSize);
    loadTodayBookings(1, newSize);
  };
  
  if (loading) {
    return (
      <div className="dashboard-main-content">
        <div className="loading-state">
          <h3>{t("admin.dashboard.loading_title")}</h3>
          <p>{t("admin.dashboard.loading_desc")}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="dashboard-main-content">
        <div className="error-state">
          <h3>{t("admin.dashboard.error_title")}</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={loadDashboardData}>
            {t("admin.dashboard.retry")}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-main-content">
      <div className="dashboard-header">
        <h1>{t("admin.dashboard.title")}</h1>
        <p>{t("admin.dashboard.subtitle")}</p>
      </div>
      
      <div className="dashboard-layout">
        <div className="dashboard-left">
          {/* Stats Cards */}
          <div className="stats-grid">
            <div 
              className="stat-card clickable" 
              onClick={handlePendingBookingsClick}
              title="Click to view today's bookings"
            >
              <div className="stat-icon pending">
                <img src='/icons/calendar.png' alt="" className="" />
              </div>
              <div className="stat-content">
                <h3>{t("admin.dashboard.pending_bookings")}</h3>
                <div className="stat-number">{dashboardData.pendingBookings}</div>
                <p>{t("admin.dashboard.pending_desc")}</p>
              </div>
            </div>
            
            <div 
              className="stat-card clickable" 
              onClick={handleAvailableTablesClick}
              title="Click to view tables"
            >
              <div className="stat-icon available">
                <img src='/icons/desk.png' alt="" className="" />
              </div>
              <div className="stat-content">
                <h3>{t("admin.dashboard.available_tables")}</h3>
                <div className="stat-number">{dashboardData.availableTables}</div>
                <p>{t("admin.dashboard.available_desc")}</p>
              </div>
            </div>
            
            <div 
              className="stat-card clickable"
              onClick={handleCustomersClick}
              title="Click to view customers"
            >
              <div className="stat-icon customers">                
                <img src='/icons/persons.png' alt="" className="" />
              </div>
              <div className="stat-content">
                <h3>{t("admin.dashboard.new_customers")}</h3>
                <div className="stat-number">{dashboardData.newCustomersThisMonth}</div>
                <p>{t("admin.dashboard.new_customers_desc")}</p>
              </div>
            </div>
          </div>
          
          {/* Today's Bookings Table with Pagination */}
          <div className="today-bookings-section">
            <div className="section-header">
              <h2>{t("admin.dashboard.today_bookings")}</h2>
              <p>{t("admin.dashboard.total")}: {todayBookingsTotalItems} {t("admin.dashboard.bookings")}</p>
            </div>
            
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("admin.dashboard.booking_id")}</th>
                    <th>{t("admin.dashboard.customer")}</th>
                    <th>{t("admin.dashboard.phone_number")}</th>
                    <th>{t("admin.dashboard.booking_date")}</th>
                    <th>{t("admin.dashboard.status")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.todayBookings.length > 0 ? (
                    dashboardData.todayBookings.map((booking) => (
                      <BookingRow
                        key={booking.bookingId}
                        booking={booking}
                        onShowDetails={handleShowDetails}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="empty-state">
                        <h3>{t("admin.dashboard.no_bookings_today")}</h3>
                        <p>{t("admin.dashboard.no_bookings_desc")}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination for Today's Bookings */}
            {todayBookingsTotalItems > 0 && (
              <Pagination
                currentPage={todayBookingsPage}
                totalPages={todayBookingsTotalPages}
                totalItems={todayBookingsTotalItems}
                itemsPerPage={todayBookingsPageSize}
                onPageChange={handleTodayBookingsPageChange}
                onItemsPerPageChange={handleTodayBookingsPageSizeChange}
              />
            )}
          </div>
        </div>
        
        {/* Top Customers Sidebar */}
        <div className="dashboard-right">
          <div className="top-customers-section">
            <div className="section-header">
              <h2>{t("admin.dashboard.top_customers")}</h2>
              <p>{t("admin.dashboard.top_customers_desc")}</p>
            </div>
            
            <div className="customers-list">
              {dashboardData.topCustomers.length > 0 ? (
                dashboardData.topCustomers.slice(0, 5).map((customer, index) => (
                  <div key={customer.customerId || index} className="customer-card">
                    <div className="customer-rank">#{index + 1}</div>
                    <div className="customer-info">
                      <h4>{customer.customerName}</h4>
                      <p>{customer.phoneNumber}</p>
                      <div className="customer-stats">
                        <span className="stats">
                          {customer.loyaltyPoints}
                        </span>
                        <span className="stats">
                          {translateMembershipLevel(t,customer.membershipLevel)}
                        </span>
                      </div>
                    </div>
                    <button 
                      className="view-details-btn"
                      onClick={() => handleViewCustomerDetails(customer)}
                    >
                      {t("admin.dashboard.view_details")}
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-customers">
                  <p>{t("admin.dashboard.no_customers")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Booking Modal for viewing/editing details */}
      {isBookingModalOpen && (
        <BookingModal
          booking={selectedBooking}
          mode={bookingModalMode}
          onSave={handleSaveBooking}
          onDelete={handleDeleteFromModal}
          onClose={closeBookingModal}
        />
      )}
      
      {/* Customer Modal for viewing details */}
      {isCustomerModalOpen && (
        <CustomerModal
          customer={selectedCustomer}
          mode={customerModalMode}
          onSave={() => {}} // Disable save in dashboard
          onDelete={() => {}} // Disable delete in dashboard
          onClose={closeCustomerModal}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && bookingToDelete && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation">
            <div className="warning-icon"><FaExclamationTriangle style={{ fontSize: '48px', color: '#dc2626' }} /></div>
            <h3>{t('admin.reports.recent.confirm_delete_title')}</h3>
            <p>
              {t('admin.reports.recent.confirm_delete_msg', {
                id: bookingToDelete.bookingId,
                name: bookingToDelete.customer?.customerName || bookingToDelete.customerName
              })}
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDelete}>
                {t('admin.reports.recent.cancel')}
              </button>
              <button className="btn-delete" onClick={confirmDelete}>
                {t('admin.reports.recent.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;